import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { DeferNotificationsProptypes } from 'src/components/JobForm/DeferNotifications/propTypes';
import { DeferTimerMessage } from 'src/components/NotificationsDeferPopup/types';
import { NotificationsDeferPopup } from 'src/components/NotificationsDeferPopup/NotificationsDeferPopup';
import { secondsToDuration } from 'src/components/JobForm/DeferNotifications/utils';
import { useJobNotificationDeferment } from 'src/hooks/jobNotificationDeferments/useJobNotificationDeferment';
import { useJobNotificationDeleteDeferment } from 'src/hooks/jobNotificationDeferments/useJobNotificationDeleteDeferment';
import s from 'src/components/JobForm/JobForm.styles';

const deferTimerWorker = new Worker(new URL('../../../workers/deferTimer.worker', import.meta.url), {type: 'module'});

export const DeferNotifications = ({ jobId }) => {
  const { t } = useTranslation();

  const [defermentCountdown, setDefermentCountdown] = useState(-1);
  const [showDeferNotificationsPopup, setShowDeferNotificationsPopup] = useState(false);
  const [notificationsDeferment, setNotificationsDeferment] = useState(undefined);
  const { data: jobNotificationDefermentResponse } = useJobNotificationDeferment({ jobId });

  const deleteNotificationDeferment = useJobNotificationDeleteDeferment();

  const onRemoveJobDeferment = async () => {
    const deleteResponse = await deleteNotificationDeferment({ jobId });

    if (deleteResponse.data?.deleteJobNotificationDeferment?.success) {
      deferTimerWorker.postMessage({
        type: DeferTimerMessage.ClearCountdown,
      });
      setNotificationsDeferment(undefined);
      setDefermentCountdown(-1);
    }
  };

  const onGetWorkerMessage = (event) => {
    const { type, value } = event.data;

    if (type === DeferTimerMessage.ReturnCountdownValue) {
      setDefermentCountdown(value);
    } else if (type === DeferTimerMessage.StopCountdown) {
      onRemoveJobDeferment();
    }
  };

  useEffect(() => {
    deferTimerWorker.addEventListener('message', onGetWorkerMessage);

    return () => {
      deferTimerWorker.removeEventListener('message', onGetWorkerMessage);
    };
  }, []);

  useEffect(() => {
    if (jobNotificationDefermentResponse?.userJobNotificationDeferment) {
      setNotificationsDeferment(jobNotificationDefermentResponse.userJobNotificationDeferment);
    }
  }, [jobNotificationDefermentResponse]);

  useEffect(() => {
    if (notificationsDeferment) {
      const currentTime = moment.utc();
      const deferUntilTime = moment.utc(notificationsDeferment.deferUntil);

      if (currentTime.isBefore(deferUntilTime)) {
        const differenceInSeconds = deferUntilTime.diff(currentTime) / 1000;

        deferTimerWorker.postMessage({
          type: DeferTimerMessage.StartCountdown,
          value: Math.floor(differenceInSeconds),
        });

      } else {
        onRemoveJobDeferment();
      }
    }
  }, [notificationsDeferment]);

  const deferNotificationsPopup = (
    <NotificationsDeferPopup
      jobId={jobId}
      onClose={() => setShowDeferNotificationsPopup(false)}
      setNotificationsDeferment={setNotificationsDeferment}
    />
  );

  return (
    <Fragment>
      {showDeferNotificationsPopup && deferNotificationsPopup}
      <s.Status>
        {defermentCountdown >= 0 && (
          <s.DeferNotificationWrapper>
            <s.DeferNotificationLabel>{t('REVIEW_NOTIFICATIONS_ON_HOLD')}</s.DeferNotificationLabel>
            <s.DeferNotificationCountdown>{secondsToDuration(defermentCountdown)}</s.DeferNotificationCountdown>
          </s.DeferNotificationWrapper>
        )}
        {defermentCountdown >= 0 && (
          <s.StatusButton
            onClick={onRemoveJobDeferment}
            orange
            sm
            solid
            type="button"
          >
            {t('REACTIVATE_NOTIFICATIONS')}
          </s.StatusButton>
        )}
        {defermentCountdown === -1 && (
          <s.StatusButton
            onClick={() => setShowDeferNotificationsPopup(true)}
            orange
            sm
            solid
            type="button"
          >
            {t('DEFER_NOTIFICATIONS')}
          </s.StatusButton>
        )}
      </s.Status>
    </Fragment>
  );
};

DeferNotifications.propTypes = DeferNotificationsProptypes;
